import { useCallback, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLogger } from './useLogger';
import { useAuthAppContext } from './useAuthAppContext';
import { USER_INFO_QUERY_KEY, UserInfoContext } from './UserInfoQueryContext.context';
import { EdiUserAccountService } from '../services/EdiUserAccountService';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from '../types/appRoutes';
import { AuthenticationError } from '../types/AuthTypes';
import { UserInfos } from '../types/UserInfos';

export const UserInfoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const logger = useLogger();
  const navigate = useNavigate();
  const { isAuthenticated: isAuth0Authenticated, logout } = useAuth0();
  const { authMethod, getAuthToken, clearKeyAuth, sessionStatus } = useAuthAppContext();

  // Determine if user is authenticated via any method
  const isAuthenticated = isAuth0Authenticated || authMethod === 'key';

  const userApiService = useMemo(
    () => new EdiUserAccountService(getAuthToken, logger),
    [getAuthToken, logger],
  );

  const queryClient = useQueryClient();
  const userInfoQuery = useQuery({
    queryKey: USER_INFO_QUERY_KEY,
    queryFn: () => userApiService.fetchUserConnectInfos(),
    // On attend une session vérifiée (sessionStatus === 'valid') plutôt que le seul flag
    // isAuthenticated, pour ne pas déclencher un appel API voué à échouer sur une session expirée.
    enabled: isAuthenticated && sessionStatus === 'valid',

    // Configuration pour que les erreurs remontent à l'ErrorBoundary
    throwOnError: true,

    // Configuration des retry
    retry: (failureCount: number, error: Error) => {
      const errorMessage = error.message.toLowerCase();

      logger.warn('UserInfoProvider: useQuery error message', error);

      // Ne pas retry sur certaines erreurs
      if (
        error instanceof AuthenticationError ||
        errorMessage.includes('401') ||
        errorMessage.includes('403') ||
        errorMessage.includes('404')
      ) {
        return false;
      }

      return failureCount < 3;
    },

    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),

    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const refetchUserInfos = useCallback(async () => {
    await queryClient.refetchQueries({ queryKey: USER_INFO_QUERY_KEY });
    return queryClient.getQueryData<UserInfos>(USER_INFO_QUERY_KEY) ?? null;
  }, [queryClient]);

  const handleLogout = useCallback(async () => {
    try {
      // Invalider le cache des utilisateurs
      // queryClient.invalidateQueries({ queryKey: USER_INFO_QUERY_KEY });
      queryClient.removeQueries({ queryKey: USER_INFO_QUERY_KEY });

      if (authMethod === 'key') {
        // Key-based authentication: clear key and redirect to login
        logger.info('Logging out from key-based authentication');
        clearKeyAuth();
        await navigate(AppRoutes.routeLogin);
      } else {
        // Auth0 authentication: use Auth0 logout
        logger.info('Logging out from Auth0 authentication');
        await logout({
          logoutParams: { returnTo: window.location.origin },
        });
      }
    } catch (error) {
      logger.error('Logout failed:', error);
    }
  }, [queryClient, authMethod, logger, clearKeyAuth, navigate, logout]);

  const contextValue = useMemo(
    () => ({
      userInfos: userInfoQuery.data || null,
      isLoading: userInfoQuery.isLoading,
      refetchUserInfo: refetchUserInfos,
      handleLogout: handleLogout,
      getAccessToken: getAuthToken,
    }),
    [userInfoQuery.data, userInfoQuery.isLoading, refetchUserInfos, handleLogout, getAuthToken],
  );

  return <UserInfoContext.Provider value={contextValue}>{children}</UserInfoContext.Provider>;
};
